import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SaveIcon from "@material-ui/icons/Save";
import EventIcon from "@material-ui/icons/Event";
import * as api from "../../api/index";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    marginTop: theme.spacing(10),
    maxWidth: 500,
    margin: "auto",
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 12,
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
    color: "#333",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  statusBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    marginBottom: theme.spacing(3),
    textAlign: "center",
  },
  activeStatus: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #4caf50",
  },
  expiredStatus: {
    backgroundColor: "#ffebee",
    border: "1px solid #f44336",
  },
  warningStatus: {
    backgroundColor: "#fff3e0",
    border: "1px solid #ff9800",
  },
  saveButton: {
    backgroundColor: "#ef7f1a",
    color: "#fff",
    fontWeight: "bold",
    padding: "12px 30px",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#d66a0a",
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  chip: {
    fontWeight: "bold",
    fontSize: 14,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

const Subscription = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOption, setSelectedOption] = useState("1");
  const [customDate, setCustomDate] = useState("");
  const [currentExpiry, setCurrentExpiry] = useState(null);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const { data } = await api.getSubscription();
      const sub = data.data;
      if (sub.subscriptionExpiry) {
        setCurrentExpiry(new Date(sub.subscriptionExpiry));
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionChange = (e) => {
    setSelectedOption(e.target.value);
    if (e.target.value !== "custom") {
      setCustomDate("");
    }
  };

  const calculateExpiryDate = () => {
    if (selectedOption === "custom") {
      return customDate ? new Date(customDate) : null;
    }
    const days = parseInt(selectedOption);
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + days);
    return expiry;
  };

  const handleSave = async () => {
    const expiryDate = calculateExpiryDate();
    if (!expiryDate) {
      alert("Please select a date");
      return;
    }

    try {
      setSaving(true);
      await api.updateSubscription({
        subscriptionType: selectedOption === "custom" ? "custom" : "days",
        subscriptionValue: selectedOption === "custom" ? 0 : parseInt(selectedOption),
        subscriptionExpiry: expiryDate,
        isActive: true,
      });
      setCurrentExpiry(expiryDate);
      alert("Subscription updated!");
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const getStatus = () => {
    if (!currentExpiry) {
      return { label: "No Expiry Set", class: classes.warningStatus };
    }

    const today = new Date();
    const diff = currentExpiry.getTime() - today.getTime();
    const daysLeft = Math.ceil(diff / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { label: `Expired ${Math.abs(daysLeft)} days ago`, class: classes.expiredStatus };
    } else if (daysLeft <= 10) {
      return { label: `Expires in ${daysLeft} days`, class: classes.warningStatus };
    } else {
      return { label: `Active - ${daysLeft} days left`, class: classes.activeStatus };
    }
  };

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress style={{ color: "#ef7f1a" }} />
      </Box>
    );
  }

  const status = getStatus();

  return (
    <Box className={classes.container}>
      <Card className={classes.card} elevation={3}>
        <CardContent>
          <Typography variant="h5" className={classes.title}>
            <EventIcon style={{ color: "#ef7f1a" }} />
            Subscription
          </Typography>

          {/* Current Status */}
          <Box className={`${classes.statusBox} ${status.class}`}>
            <Chip label={status.label} className={classes.chip} />
            {currentExpiry && (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                Expiry: {currentExpiry.toLocaleDateString()}
              </Typography>
            )}
          </Box>

          {/* Dropdown */}
          <TextField
            select
            fullWidth
            label="Extend Subscription"
            value={selectedOption}
            onChange={handleOptionChange}
            variant="outlined"
            className={classes.formField}
          >
            <MenuItem value="1">1 Day</MenuItem>
            <MenuItem value="2">2 Days</MenuItem>
            <MenuItem value="3">3 Days</MenuItem>
            <MenuItem value="4">4 Days</MenuItem>
            <MenuItem value="5">5 Days</MenuItem>
            <MenuItem value="custom">Custom Date</MenuItem>
          </TextField>

          {/* Custom Date Picker */}
          {selectedOption === "custom" && (
            <TextField
              fullWidth
              type="date"
              label="Select Date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              className={classes.formField}
            />
          )}

          {/* Save Button */}
          <Button
            fullWidth
            variant="contained"
            className={classes.saveButton}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Subscription;
